import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Attribute, AttributeGroup, Category, CategorySelection, Option, ProductFormValues } from "components/products/types";
import client from "api/client";
// Import static categories instead of using GraphQL query
import staticCategoriesData from "../../../data/staticCategories.json";
import { GET_FEATURES_QUERY } from "api/graphql/queries/Getfeatures";
import { fetchAttributeOptions } from "utils/fetchAttributeOptions";
import { 
  debounce, 
  fetchOptions
} from "./productFormUtils";
import { toast } from "react-toastify";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { ADD_SELLER_PRODUCT_MUTATION } from "api/graphql/mutations/AddSellerProductMutation";
import { FileAttachment } from "components/custom/types";

interface UseProductFormProps {
  commonAttributeGroups: AttributeGroup[];
  allTabGroups: AttributeGroup[];
}

const useProductForm = ({ commonAttributeGroups, allTabGroups }: UseProductFormProps) => {
  const { t } = useTranslation();
  
  // Form state
  const [formValues, setFormValues] = useState<ProductFormValues>({});
  
  // Loading state
  const [loading, setLoading] = useState(false);

  // Category and attribute states
  const [characteristicsAttributes, setCharacteristicsAttributes] = useState<Attribute[]>([]);
  const [variationAttributes, setVariationAttributes] = useState<Attribute[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<Attribute[]>([]);
  const [initialCategorySelection, setInitialCategorySelection] = useState<CategorySelection>({
    level2: null,
    level3: null,
    level4: null,
  });
  
  // Options and variants states
  const [dynamicOptions, setDynamicOptions] = useState<{ [key: string]: Option[] }>({});
  const [brandOptions, setBrandOptions] = useState<Option[]>([]);
  const [generatedVariants, setGeneratedVariants] = useState<any[]>([]);
  
  // Loading states
  const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState<boolean>(false);
  
  // Validation states
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [variantCreationError, setVariantCreationError] = useState<string | null>(null);
  
  // Apollo hooks
  const [addSellerProduct, { loading: mutationLoading, data, error }] = useMutation(ADD_SELLER_PRODUCT_MUTATION);

  // Load initial data when component mounts
  useEffect(() => {
    // Load brand options
    const loadBrandOptions = async () => {
      try {
        setIsLoadingOptions(true);
        const options = await fetchAttributeOptions("brand");
        setBrandOptions(options);
      } catch (error) {
        console.error("Error fetching brand options:", error);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadBrandOptions();
  }, []);

  // Field change handler
  const handleChange = useCallback((attribute_code: string, value: any) => {
    // Special handling for brand to ensure both id and name are saved
    if (attribute_code === 'brand' && Array.isArray(value) && value.length > 0) {
      // We want to preserve both id and name for brand options
      console.log("Setting brand with ID and name:", value);
      
      setFormValues((prevValues) => ({
        ...prevValues,
        [attribute_code]: value.map(option => ({
          option_id: option.option_id,
          value: option.value  // This is the brand name
        })),
      }));
    } 
    // Special handling for product name to ensure it's properly saved
    else if (attribute_code === 'name') {
      console.log("Setting product name:", value);
      // Ensure we always have a string value
      const nameValue = String(value || '');
      
      setFormValues((prevValues) => ({
        ...prevValues,
        [attribute_code]: nameValue,
      }));
    }
    // Validate numeric fields for offer and logistics to ensure they're greater than 0
    else if (['price', 'special_price', 'weight', 'dimensions_de_l_outil_largeur1', 
              'dimensions_de_l_outil_hauteur1', 'longueur25', 'quantity_and_stock_status'].includes(attribute_code)) {
      // Convert to number and check if valid and <= 0
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (value && !isNaN(numValue) && numValue <= 0) {
        // Get translated field name and show warning toast
        // Map the attribute code to a translatable name using a field map
        const fieldLabelMap: Record<string, string> = {
          'price': 'Price',
          'special_price': 'Special Price',
          'weight': 'Weight',
          'dimensions_de_l_outil_largeur1': 'Width',
          'dimensions_de_l_outil_hauteur1': 'Height',
          'longueur25': 'Length',
          'quantity_and_stock_status': 'Quantity'
        };
        const fieldLabel = fieldLabelMap[attribute_code] || t(attribute_code);
        toast.warning(`${fieldLabel} must be greater than 0`);
        
        // Add to invalidFields for immediate validation feedback
        setInvalidFields(prev => {
          if (!prev.includes(attribute_code)) {
            return [...prev, attribute_code];
          }
          return prev;
        });
      } else if (value && !isNaN(numValue) && numValue > 0) {
        // Clear from invalidFields if value is now valid
        setInvalidFields(prev => prev.filter(field => field !== attribute_code));
      }
      
      setFormValues((prevValues) => ({
        ...prevValues,
        [attribute_code]: value,
      }));
    }
    else {
      // Normal handling for other attributes
      setFormValues((prevValues) => ({
        ...prevValues,
        [attribute_code]: value,
      }));
    }
    
    // Clear this field from invalidFields when it's corrected
    if (invalidFields.includes(attribute_code)) {
      setInvalidFields(prev => prev.filter(field => field !== attribute_code));
      
      // If this was the last invalid field, clear the variant creation error
      if (invalidFields.length === 1 && invalidFields[0] === attribute_code) {
        setVariantCreationError(null);
      }
    }
  }, [invalidFields]);

  // Input change handler with debounce for typeahead components
  const handleInputChange = useCallback(
    debounce(async (query: string, attribute: Attribute) => {
      if (attribute.options_getter) {
        setIsLoadingOptions(true);
        const options = await fetchOptions(attribute.options_getter, query) || [];
        setDynamicOptions((prev) => ({
          ...prev,
          [attribute.attribute_code]: options,
        }));
        setIsLoadingOptions(false);
      }
    }, 500),
    []
  );

  // Categories fetch handler - using static JSON data instead of GraphQL query
  const fetchCategories = async (): Promise<Category[]> => {
    try {
      console.log("Using static categories data instead of GraphQL query");
      
      // The JSON file directly contains an array of categories
      const categoriesArray = staticCategoriesData as any[];
      
      if (!Array.isArray(categoriesArray) || categoriesArray.length === 0) {
        console.warn("Static categories data is empty or not an array");
        return [];
      }
      
      // The structure is an array where the first item contains all the categories
      // We need to get the children of this root category, which are the level 2 categories
      const rootCategory = categoriesArray[0];
      if (!rootCategory || !Array.isArray(rootCategory.children)) {
        console.warn("Root category has no children");
        return [];
      }
      
      // Filter level 2 categories that should be included in menu
      const categories = rootCategory.children
        .filter((category: any) => category.level === 2 && category.include_in_menu === 1);
      
      console.log(`Found ${categories.length} level 2 categories`);
      return categories;
    } catch (error) {
      console.error("Error fetching categories from static data:", error);
      return [];
    }
  };

  // Features fetch handler for a selected category
  const fetchFeatures = async (categoryId: string) => {
    try {
      if (!categoryId) {
        console.error("Category ID is required to fetch features.");
        return [];
      }

      const { data } = await client.query({
        query: GET_FEATURES_QUERY,
        variables: { categoryId },
      });

      if (data?.getFeatures) {
        return data.getFeatures;
      }

      console.error("No features found for the given category ID.");
      return [];
    } catch (error) {
      console.error("Error fetching features:", error);
      return [];
    }
  };
  
  // Set up ref to track if initial setup is complete
  const initialSetupComplete = useRef(false);
  
  // Auto-select saved category after all initial setup is done
  useEffect(() => {
    // Only run this once and only if we have initialCategorySelection
    if (!initialSetupComplete.current && 
        initialCategorySelection && 
        initialCategorySelection.level2) {
      
      console.log("Auto-selecting saved category on mount:", initialCategorySelection);
      
      // Delay the category selection to ensure all other initialization is complete
      setTimeout(() => {
        handleCategorySelection(initialCategorySelection);
        initialSetupComplete.current = true;
      }, 100);
    }
  }, [initialCategorySelection]);

  // Category selection handler
  const handleCategorySelection = async (selectedCategory: {
    level2: Category | null;
    level3: Category | null;
    level4: Category | null;
  }) => {
    console.log("Selected Category from UI:", selectedCategory);

    // Check if the selection is incomplete (less than 3 levels)
    const isIncomplete = !selectedCategory.level3;
    
    // For ALL category selections, first update the UI state
    if (isIncomplete) {
      // Handle incomplete category selections (1 or 2 levels)
      console.log("Category not complete - at least level 3 must be selected. Will be removed on save.");
      
      // We'll still update the UI to show what was selected, but not save it as valid
      // This temporary state update is just for UI navigation
      const tempFormValues = { ...formValues };
      
      // For UI navigation only - not saved to localStorage
      tempFormValues.category = {
        level2: selectedCategory.level2 ? {
          id: selectedCategory.level2.id,
          name: selectedCategory.level2.name
        } : null,
        level3: null,
        level4: null
      };
      
      setFormValues(tempFormValues);
      
      // Auto-save will run after the form state updates, which will trigger our
      // validation in debouncedSaveForm to remove category entirely from localStorage
      
      // Instead of returning early, we'll continue to the rest of the function
      // This allows us to still fetch category children for UI navigation
      return;
    }

    // Get the last available category (deepest level first)
    const lastSelectedCategory =
      selectedCategory.level4?.id ||
      selectedCategory.level3?.id;

    if (!lastSelectedCategory) {
      console.error("Error: At least Level 3 category is required!");
      toast.error("Please select a complete category (at least 3 levels).");
      return;
    }

    console.log("Complete category selected, updating form values", selectedCategory);

    // Create a cleaned category object without null levels
    const cleanCategory: Record<string, any> = {};
    
    // Only add non-null levels
    if (selectedCategory.level2) {
      cleanCategory.level2 = {
        id: selectedCategory.level2.id,
        name: selectedCategory.level2.name
      };
    }
    
    if (selectedCategory.level3) {
      cleanCategory.level3 = {
        id: selectedCategory.level3.id,
        name: selectedCategory.level3.name
      };
    }
    
    if (selectedCategory.level4) {
      cleanCategory.level4 = {
        id: selectedCategory.level4.id,
        name: selectedCategory.level4.name
      };
    }
    
    // Update formValues state with the selected category (both ID and label)
    setFormValues((prevValues) => ({
      ...prevValues,
      category: cleanCategory
    }));
    
    // Clear category from invalidFields when it's selected
    if (invalidFields.includes('category')) {
      setInvalidFields(prev => prev.filter(field => field !== 'category'));
      
      // If errors were only related to category, clear variant creation error too
      if (invalidFields.length === 1 && invalidFields[0] === 'category') {
        setVariantCreationError(null);
      }
    }

    // Fetch product attributes based on the last selected category
    try {
      setIsLoadingAttributes(true);
      const features = await fetchFeatures(lastSelectedCategory.toString());

      // Update the characteristics attributes
      setCharacteristicsAttributes(features);

      // Import productAttributes directly to ensure it's available
      const productAttrs = await import("config/productAttributes").then(module => module.default);
      
      // Map variation attributes based on fetched features
      const existingVariationAttributes =
        productAttrs.VariationAttributes.filter((variationAttr: Attribute) =>
          features.some(
            (fetchedAttr: Attribute) =>
              fetchedAttr.attribute_code === variationAttr.attribute_code
          )
        ).map((variationAttr: Attribute) => {
          const matchedAttr = features.find(
            (fetchedAttr: Attribute) =>
              fetchedAttr.attribute_code === variationAttr.attribute_code
          );
          if (matchedAttr) {
            return {
              ...variationAttr,
              options: matchedAttr.options,
            };
          }
          return variationAttr;
        });

      console.log("Updated variation attributes:", existingVariationAttributes);
      
      // Update variation attributes
      setVariationAttributes(existingVariationAttributes);

      setIsLoadingAttributes(false);
    } catch (error) {
      console.error("Error fetching features:", error);
      setIsLoadingAttributes(false);
    }
  };

  // Form clear handler
  const handleClear = () => {
    console.log("🔄 Resetting form completely...");

    // Reset form values
    setFormValues({
      name: "",
      category: { 
        level2: null, 
        level3: null, 
        level4: null 
      },
      gallery: [],
      brand: [],
      special_price: "",
      special_from_date: "",
      special_to_date: "",
      Livraison_gratuite: "",
      mode_expedition: "",
      warranty: "",
      quantity_and_stock_status: "",
      weight: "",
      dimensions_de_l_outil_largeur1: "",
      dimensions_de_l_outil_hauteur1: "",
      longueur25: "",
      selectedAttributes: [],
      variantsData: [],
    });

    // Reset additional states
    setGeneratedVariants([]);
    setSelectedAttributes([]);
    setInitialCategorySelection({ level2: null, level3: null, level4: null });
    setInvalidFields([]);
    setVariantCreationError(null);
    
    // Reset characteristics and variation attributes
    setCharacteristicsAttributes([]); 
    setVariationAttributes([]);
    
    // Reset initialSetupComplete flag to prevent auto-selection of previously saved category
    initialSetupComplete.current = false;

    console.log("✅ Form reset completely!");
  };

  // Save draft handler
  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      toast.success("Draft saved successfully");
    } catch (error) {
      console.error("Error saving draft:", error);
      toast.error("Failed to save draft");
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const validateForm = () => {
    const invalidFields: string[] = [];
    
    // Check if category is selected and complete (must have at least level 3)
    if (!formValues.category?.level2 || !formValues.category.level2.id || 
        !formValues.category?.level3 || !formValues.category.level3.id) {
      invalidFields.push('category');
    }

    // Validate fields in common attribute groups
    commonAttributeGroups.forEach((group) => {
      if (typeof group.attributes !== "string") {
        const excludedAttributes = ["category_ids", "sku"];
        group.attributes.forEach((attribute) => {
          if (
            attribute.is_required === "1" &&
            !formValues[attribute.attribute_code] &&
            !excludedAttributes.includes(attribute.attribute_code)
          ) {
            invalidFields.push(attribute.attribute_code);
          }
        });
      }
    });

    // Validate fields in ProductTabs
    allTabGroups.forEach((group) => {
      if (typeof group.attributes !== "string") {
        group.attributes.forEach((attribute) => {
          if (
            attribute.is_required === "1" &&
            !formValues[attribute.attribute_code]
          ) {
            invalidFields.push(attribute.attribute_code);
          }
        });
      }
    });

    // Special validation: When special_price is set, special_from_date and special_to_date are required
    if (formValues["special_price"] && formValues["special_price"].toString().trim() !== '') {
      if (!formValues["special_from_date"]) {
        invalidFields.push("special_from_date");
      }
      if (!formValues["special_to_date"]) {
        invalidFields.push("special_to_date");
      }
    }

    // Validate numeric fields for both required and non-required - must be greater than 0 if filled
    const numericFields = ["price", "special_price", "weight", 
                          "dimensions_de_l_outil_largeur1", 
                          "dimensions_de_l_outil_hauteur1", 
                          "longueur25", "quantity_and_stock_status"];
    
    numericFields.forEach(field => {
      if (formValues[field] !== undefined && formValues[field] !== '') {
        const value = parseFloat(formValues[field]);
        if (value <= 0 || isNaN(value)) {
          if (!invalidFields.includes(field)) {
            invalidFields.push(field);
          }
        }
      }
    });

    return invalidFields;
  };

  // Validate required fields for variants
  const validateRequiredFieldsForVariants = () => {
    // Create a new array to collect all currently invalid fields
    const currentInvalidFields: string[] = [];
    
    // Required fields that must be filled before creating variants
    const requiredFields = [
      'name',                             // Product Name
      'reference',                        // Seller Reference
      'price',                            // Product Price
      'short_description',                // Short Description
      'description',                      // Description
      'weight',                           // Weight
      'mode_expedition',                  // Shipping Method
      'dimensions_de_l_outil_largeur1',   // Package Width
      'dimensions_de_l_outil_hauteur1',   // Package Height
      'longueur25'                        // Package Depth
    ];
    
    // Check if category is selected and complete (must have at least level 3)
    if (!formValues.category?.level2 || !formValues.category.level2.id || 
        !formValues.category?.level3 || !formValues.category.level3.id) {
      currentInvalidFields.push('category');
    }
    
    // Check required fields from common attributes
    const missingFields = requiredFields.filter(field => {
      return !formValues[field] || formValues[field] === '';
    });
    
    if (missingFields.length > 0) {
      // Add all missing fields to our current invalid fields array
      currentInvalidFields.push(...missingFields);
    }
    
    // Additional validation for price - must be greater than 0
    if (formValues.price !== undefined && formValues.price !== '' && 
        (parseFloat(formValues.price) <= 0 || isNaN(parseFloat(formValues.price)))) {
      if (!currentInvalidFields.includes('price')) {
        currentInvalidFields.push('price');
      }
    }
    
    // If special price is filled, it should also be greater than 0
    if (formValues.special_price !== undefined && formValues.special_price !== '' && 
        (parseFloat(formValues.special_price) <= 0 || isNaN(parseFloat(formValues.special_price)))) {
      if (!currentInvalidFields.includes('special_price')) {
        currentInvalidFields.push('special_price');
      }
    }
    
    // Validate dimensions and weight - must be greater than 0 if filled
    const logisticsFields = ['weight', 'dimensions_de_l_outil_largeur1', 'dimensions_de_l_outil_hauteur1', 'longueur25'];
    logisticsFields.forEach(field => {
      if (formValues[field] !== undefined && formValues[field] !== '' && 
          (parseFloat(formValues[field]) <= 0 || isNaN(parseFloat(formValues[field])))) {
        if (!currentInvalidFields.includes(field)) {
          currentInvalidFields.push(field);
        }
      }
    });
    
    // If gallery is required, check it
    const galleryRequired = commonAttributeGroups.some(group => {
      if (typeof group.attributes !== "string") {
        return group.attributes.some(attr => 
          attr.attribute_code === "gallery" && 
          attr.is_required === "1" && 
          (!formValues.gallery || formValues.gallery.length === 0)
        );
      }
      return false;
    });
    
    if (galleryRequired && (!formValues.gallery || formValues.gallery.length === 0)) {
      currentInvalidFields.push('gallery');
    }
    
    // Check common attribute groups for any other required fields
    commonAttributeGroups.forEach((group) => {
      if (typeof group.attributes !== "string") {
        const excludedAttributes = ["category_ids", "sku", ...requiredFields]; // Skip fields we've already checked
        group.attributes.forEach((attribute) => {
          if (
            attribute.is_required === "1" &&
            !formValues[attribute.attribute_code] &&
            !excludedAttributes.includes(attribute.attribute_code)
          ) {
            currentInvalidFields.push(attribute.attribute_code);
          }
        });
      }
    });
    
    // Check if any tab attributes are required
    allTabGroups.forEach((group) => {
      if (typeof group.attributes !== "string") {
        group.attributes.forEach((attribute) => {
          if (
            attribute.is_required === "1" &&
            !formValues[attribute.attribute_code] &&
            !currentInvalidFields.includes(attribute.attribute_code)
          ) {
            currentInvalidFields.push(attribute.attribute_code);
          }
        });
      }
    });
    
    // After collecting all invalid fields, update the state
    setInvalidFields(currentInvalidFields);
    
    // No error message is returned - we'll highlight the fields instead
    return currentInvalidFields.length > 0;
  };
  
  // Check if any fields in a specific tab group are missing
  const checkForTabErrors = (invalidFields: string[], tabName: string): boolean => {
    // Define fields for each tab
    const tabFieldsMap: Record<string, string[]> = {
      'Offre': [
        'price',
        'special_price',
        'special_from_date', 
        'special_to_date',
      ],
      'Informations Logistiques': [
        'mode_expedition',
        'weight',
        'dimensions_de_l_outil_largeur1',
        'dimensions_de_l_outil_hauteur1',
        'longueur25'
      ]
    };
    
    const tabFields = tabFieldsMap[tabName] || [];
    return tabFields.some(field => invalidFields.includes(field));
  };

  // Check tabs in order of priority
  const determineTabToShow = (invalidFields: string[]): string | null => {
    // First check Offre tab
    if (checkForTabErrors(invalidFields, 'Offre')) {
      return 'Offre';
    }
    
    // Then check Informations Logistiques tab
    if (checkForTabErrors(invalidFields, 'Informations Logistiques')) {
      return 'Informations Logistiques';
    }
    
    return null; // No specific tab needs to be shown
  };

  // Create variants handler
  const handleCreateVariants = (variants: any[], selectedAttrs: Attribute[]) => {
    console.log("handleCreateVariants called with:", { variants, selectedAttrs });
    
    // Initialize, but don't validate variant fields yet
    const variantInvalidFields: string[] = [];
    
    // Save current state of invalidFields
    const currentInvalidFields = [...invalidFields];
    
    // First, validate only required fields from the main form (not variant-related)
    const mainFormInvalid = validateRequiredFieldsForVariants(); 
    
    // If main form has errors, handle those first and don't check variants yet
    if (mainFormInvalid) {
      // Show a simple error message
      toast.error("Please fill in the required fields");
      
      // Check tabs in order of priority
      const tabToShow = determineTabToShow(invalidFields);
      
      if (tabToShow) {
        // Dispatch a custom event that the AddProduct component can listen for
        const tabSwitchEvent = new CustomEvent('switch-to-tab', { 
          detail: { tabName: tabToShow } 
        });
        document.dispatchEvent(tabSwitchEvent);
        
        // Wait a bit before scrolling to make sure the tab has switched
        setTimeout(() => {
          const firstErrorElement = document.querySelector('.is-invalid, .is-invalid-container, .is-invalid-wrapper');
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
      } else {
        // Just scroll to the first error field
        setTimeout(() => {
          const firstErrorElement = document.querySelector('.is-invalid, .is-invalid-container, .is-invalid-wrapper');
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      
      return; // Stop the creation process if main form validation fails
    }
    
    // Clear any previous errors
    setVariantCreationError(null);

    console.log("Raw variants received:", variants);
    console.log("Selected attributes received:", selectedAttrs);
    
    // Check if variants array is empty, which means the validation check was called
    // without actual variants (just to trigger required field validation)
    if (variants.length === 0) {
      console.log("Empty variants array - this was just a validation check");
      return;
    }
    
    // Add unique IDs to variants for tracking in the table
    const fixedVariants = variants.map((variant) => ({
      ...variant,
      id: variant.id || Math.random().toString(36).substr(2, 9), // Ensure unique ID
      quantity: variant.quantity ? parseInt(variant.quantity, 10) : 1,
      price: variant.price ? parseFloat(variant.price) : 0,
    }));

    console.log("Setting generated variants:", fixedVariants);
    
    // First, clear the existing variants
    setGeneratedVariants([]);
    
    // Then set the new ones (this ensures React will see it as a change)
    setTimeout(() => {
      setGeneratedVariants(fixedVariants);
      
      // Also update the selected attributes
      setSelectedAttributes(selectedAttrs);
      
      // Save to form values correctly
      setFormValues((prev) => ({
        ...prev,
        variantsData: fixedVariants,
        selectedAttributes: selectedAttrs,
      }));
      
      console.log("Updated state with new variants:", fixedVariants.length);
    }, 0);
    
    // Scroll to the variants table
    setTimeout(() => {
      const variantsTable = document.querySelector('.product-variants-table');
      if (variantsTable) {
        variantsTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  };

  // Remove variant handler
  const handleRemoveVariant = (index: number) => {
    const updatedVariants = [...generatedVariants];
    updatedVariants.splice(index, 1);
    setGeneratedVariants(updatedVariants);
    
    const updatedFormValues = { ...formValues };
    delete updatedFormValues[`selectedAttributeOptions_${index}`];
    delete updatedFormValues[`variantAttribute_${index}`];
    
    if (index === 0 && formValues.variantAttribute_1) {
      updatedFormValues.variantAttribute_0 = formValues.variantAttribute_1;
      delete updatedFormValues.variantAttribute_1;
      updatedFormValues.selectedAttributeOptions_0 =
        formValues.selectedAttributeOptions_1;
      delete updatedFormValues.selectedAttributeOptions_1;
    } else if (index === 1) {
      delete updatedFormValues.variantAttribute_1;
      delete updatedFormValues.selectedAttributeOptions_1;
    }
    
    setFormValues({
      ...updatedFormValues,
      variantsData: updatedVariants,
    });
  };

  // Delete variant handler
  const handleDeleteVariant = (compositeKey: string) => {
    console.log("Delete variant with composite key:", compositeKey);
    
    // First check if the composite key is actually a variant ID
    // (this happens when selectedAttributes is empty)
    const updatedVariants = generatedVariants.filter((variant) => {
      if (variant.id === compositeKey) {
        return false; // Remove if ID matches
      }
      
      // Otherwise check composite key
      const variantKey = selectedAttributes
        .map((attr) => variant[attr.attribute_code]?.option_id || "")
        .join("-");
      return variantKey !== compositeKey;
    });
    
    console.log(`Variants after deletion: ${updatedVariants.length} (from ${generatedVariants.length})`);
    
    // Update both states
    setGeneratedVariants(updatedVariants);
    
    // Update form values with new variants
    setFormValues((prev) => ({
      ...prev,
      variantsData: updatedVariants,
    }));
  };

  // Update variant image handler
  const handleUpdateVariantImage = (
    imagedAttributeCode: string,
    imagedAttributeValue: string,
    newImage: FileAttachment
  ) => {
    setGeneratedVariants((prevVariants) => {
      const updatedVariants = prevVariants.map((variant) => {
        if (variant[imagedAttributeCode]?.value === imagedAttributeValue) {
          return {
            ...variant,
            image: newImage,
          };
        }
        return variant;
      });
      
      setFormValues((prevValues) => ({
        ...prevValues,
        variantsData: updatedVariants,
      }));
      
      return updatedVariants;
    });
  };

  // Update variant handler
  const handleUpdateVariant = (updatedVariants: any[]) => {
    setGeneratedVariants(updatedVariants);

    setFormValues((prevValues) => ({
      ...prevValues,
      variantsData: updatedVariants,
    }));
  };

  // File drop handler
  const handleFileDrop = async (files: any[]) => {
    const updatedGallery = [...(formValues["gallery"] || [])];
    
    files.forEach((file) => {
      updatedGallery.push(file);
    });
    
    setFormValues((prevValues) => ({
      ...prevValues,
      gallery: updatedGallery,
    }));
    
    // Clear gallery from invalidFields if it was previously invalid
    if (invalidFields.includes('gallery')) {
      setInvalidFields(prev => prev.filter(field => field !== 'gallery'));
    }
  };

  // File remove handler
  const handleFileRemove = (fileName: string) => {
    setFormValues((prevValues) => {
      const gallery = prevValues["gallery"] || [];
      const updatedGallery = gallery.filter(
        (file: FileAttachment) => file.name !== fileName
      );
      return {
        ...prevValues,
        gallery: updatedGallery,
      };
    });
  };

  // File update handler
  const handleFileUpdate = (
    index: number,
    updatedData: Partial<FileAttachment>
  ) => {
    setFormValues((prevValues) => {
      const updatedGallery = [...(prevValues["gallery"] || [])];

      // Clear the roles from all other images
      if (updatedData.roles) {
        Object.keys(updatedData.roles).forEach((role) => {
          if (updatedData.roles![role]) {
            updatedGallery.forEach((file, i) => {
              if (i !== index && file.roles && file.roles![role]) {
                file.roles![role] = false;
              }
            });
          }
        });
      }

      updatedGallery[index] = {
        ...updatedGallery[index],
        ...updatedData,
      };

      return {
        ...prevValues,
        gallery: updatedGallery,
      };
    });
  };

  // File index change handler
  const handleFileIndexChange = (fromIndex: number, toIndex: number) => {
    setFormValues((prevValues) => {
      const updatedGallery = [...(prevValues["gallery"] || [])];
      const [movedFile] = updatedGallery.splice(fromIndex, 1);
      updatedGallery.splice(toIndex, 0, movedFile);

      return {
        ...prevValues,
        gallery: updatedGallery,
      };
    });
  };

  return {
    // State
    formValues,
    loading,
    characteristicsAttributes,
    variationAttributes,
    selectedAttributes,
    initialCategorySelection, // Explicitly returned for ProductAttributeGroup
    dynamicOptions,
    brandOptions,
    generatedVariants,
    isLoadingOptions,
    isLoadingAttributes,
    invalidFields,
    variantCreationError,
    
    // Handlers
    handleChange,
    handleInputChange,
    fetchCategories,
    handleCategorySelection,
    handleClear,
    handleSaveDraft,
    validateForm,
    handleCreateVariants,
    handleRemoveVariant,
    handleDeleteVariant,
    handleUpdateVariantImage,
    handleUpdateVariant,
    handleFileDrop,
    handleFileRemove,
    handleFileUpdate,
    handleFileIndexChange,
    
    // Apollo
    addSellerProduct,
    mutationLoading,
  };
};

export default useProductForm;
